import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import adminsSlice, { deleteUserByUserId, fetchAllUsers } from 'store/Admins'
import { useDispatch, useSelector } from 'react-redux'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import { EditOutlined, Delete } from '@material-ui/icons'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import { caseInsensitiveSort } from 'utils'

const tableHead = ['Full Name', 'Email', 'Actions']
const formatTableData = (admins) =>
  admins.map((admin) => [admin.fullName, admin.email, <ActionButtons admin={admin} />])

const ActionButtons = ({ admin }) => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const [deletedId, setDeletedId] = useState('')
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const handleOpenDeleteModal = (id) => {
    setOpenDeleteModal(true)
    setDeletedId(id)
  }

  const handleDeleteUser = async () => {
    dispatch(deleteUserByUserId(deletedId))
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setDeletedId('')
  }

  return (
    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
      <ConfirmModal
        dialogTitle="Delete Admin"
        dialogText="Do you really want to delete this admin?"
        open={openDeleteModal}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteUser}
      />
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() => history.push(`${location.pathname}/edit/${admin.uid}`)}>
          <EditOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => handleOpenDeleteModal(admin.uid)}>
          <Delete />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const AdminsListing = () => {
  const classes = useStyles()
  const options = getTableOptions()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const { admins, inProgress, confFilter, currentPage, pageSize } = useSelector(
    (state) => state.admins
  )

  const { conferences, inProgress: confProgress } = useSelector((state) => state.conference)
  const activeConferences = conferences?.filter((row) => row.isActive === true)
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')

  useEffect(() => {
    if (confFilter) {
      let inActive = false
      conferences.map((conf) => {
        if (conf.id === confFilter && conf.isActive === false) {
          inActive = true
        }
      })
      if (inActive && !confProgress) {
        dispatch(adminsSlice.actions.setConfFilter(orderedConferences[0]?.id))
      }
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (!confFilter && !confProgress) {
      dispatch(adminsSlice.actions.setConfFilter(orderedConferences[0]?.id))
    }
  }, [confProgress, confFilter])

  const filteredAdmins = admins?.filter((admin) => admin?.conferenceId === confFilter)

  useEffect(() => {
    dispatch(adminsSlice.actions.setIsCreatedOrUpdated(false))
    if (confFilter) {
      dispatch(fetchAllUsers(confFilter))
    }
  }, [dispatch, confFilter])

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h4">Admin Users</Typography>
        <Box className={classes.actions}>
          <Box className={classes.dateFilterDropdown}>
            <Typography className={classes.dateFilterLabel}>Conference: </Typography>
            {confProgress ? (
              <CircularProgress size={25} />
            ) : (
              <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                <Select
                  value={confFilter}
                  onChange={(event) =>
                    dispatch(adminsSlice.actions.setConfFilter(event.target.value))
                  }>
                  {orderedConferences.map((conf) => (
                    <MenuItem value={conf?.id} key={conf?.id}>
                      {conf?.confName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              if (confFilter) {
                history.push({
                  pathname: `${location.pathname}/new`,
                  search: `?conferenceId=${confFilter}`
                })
              }
            }}>
            Create Conference Admin
          </Button>
        </Box>
      </Box>
      <Wrapper inProgress={inProgress}>
        <Paper variant="outlined" style={{ marginTop: '20px' }}>
          {filteredAdmins.length ? (
            <BasicTable
              data={formatTableData(filteredAdmins)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(adminsSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(adminsSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound message="No admins available for selected conference" />
          )}
        </Paper>
      </Wrapper>
    </Box>
  )
}
export default AdminsListing

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  select: {
    minWidth: '200px'
  },
  dateFilterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  dateFilterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  }
})
